export type FormId = 'contact' | 'demo' | 'trial' | 'pricing' | 'newsletter' | 'newsletter-blog' | 'newsletter-events';

export interface FormConfig {
  redirectPath: string;
  // Work-email blocklist v1 (scratchpad 63). When true, /api/form-submit rejects
  // known free/consumer/disposable emails BEFORE forwarding to Pardot, and the
  // client shows an inline "use your work email" hint. Newsletters stay lenient —
  // individual devs / OSS contributors on gmail are wanted top-of-funnel.
  requireWorkEmail?: boolean;
  // NOTE: source-verification (which forms get a `source_verified` field appended) lives
  // server-side in form-config.server.ts (SOURCE_VERIFIED_FORMS), never here — this module
  // is imported by the client, and the list should not ship in the bundle.
}

const CONFIGS = {
  contact: {
    redirectPath: '/contact',
    requireWorkEmail: true,
  },
  // Request-a-demo form (#3426). Distinct handler + job-title field.
  // Mirrors the other proxied forms: on success/error the server redirects back to the
  // form page with ?success=true / ?error=<status>, rendered inline (no separate page).
  demo: {
    redirectPath: '/demo',
    requireWorkEmail: true,
  },
  trial: {
    redirectPath: '/develocity/trial',
    requireWorkEmail: true,
  },
  pricing: {
    redirectPath: '/pricing',
    requireWorkEmail: true,
  },
  newsletter: {
    redirectPath: '/gradle-dpe-newsletters',
  },
  'newsletter-blog': {
    redirectPath: '/blog',
  },
  'newsletter-events': {
    redirectPath: '/events',
  },
} as const satisfies Record<FormId, FormConfig>;

export const FORM_CONFIGS: Record<FormId, FormConfig> = CONFIGS;

/** Form ids with `requireWorkEmail: true`, derived from the config so it can't drift.
 * ContactForm requires `workEmailEvents` for these, so a strict form can't ship
 * without its gate-monitoring events. */
export type StrictFormId = {
  [K in FormId]: (typeof CONFIGS)[K] extends { requireWorkEmail: true } ? K : never;
}[FormId];

/**
 * Single source of truth for per-form work-email strictness — used by both the
 * authoritative server check (/api/form-submit) and the client-side inline hint,
 * so the two never drift.
 *
 * NOTE: the whitepaper/ebook + event-registration forms are ALSO strict-intent but
 * post DIRECT to Pardot (not through this proxy), so they are NOT server-covered
 * until proxied (scratchpad 63, follow-up).
 */
export function isWorkEmailRequired(formId: FormId): boolean {
  return FORM_CONFIGS[formId].requireWorkEmail ?? false;
}

/** Request header (value `fetch`) ContactForm sends on an in-page submit: /api/form-submit
 * then answers `{ location }` JSON instead of its no-JS 303. */
export const IN_PAGE_SUBMIT_HEADER = 'x-form-submit';

/** Body of /api/form-submit's in-page answer: the same-origin path + query its 303 would
 * point at (`?success=true&sid=…` or `?error=<status>[&error_reason=…]`). */
export type InPageSubmitResult = { location: string };
